import { format } from 'date-fns';
import { simpleDateToDate } from './dates';
import type { SimpleDate } from './types';

/*
  What a subscription means to a player, in words and in what they can do about it.

  The subscription itself is sold by Stripe, Apple or Google and is fetched per platform; these are
  the rules for reading one — what to say about it, and which action to offer. Shared so the app and
  the website can't tell a player different things about the same subscription, and so that a
  subscription bought on one platform reads the same on every other.

  Dates arrive as Date objects on the server and as strings once JSON has been over the wire, so both
  are accepted, and they're formatted in the reader's own timezone — a renewal is the date it happens
  where they are.
*/

export type SubscriptionStatus = 'active' | 'canceled' | 'past_due';

/** Who sold the subscription, and therefore where it can be managed. */
export type SubscriptionProvider = 'stripe' | 'apple' | 'google';

export interface Subscription {
  status: SubscriptionStatus;
  renewsAt: Date | string;
  /** Set when the subscription is running but won't renew. */
  cancelAt: Date | string | null;
  /** Absent on a subscription read straight from Stripe, which is always `stripe`. */
  provider?: SubscriptionProvider;
  /**
   * When a `past_due` subscription stops granting access. Absent when we don't know it — the live
   * Stripe read reports no such deadline — so anything shown to a player has to read without it.
   */
  graceEndsAt?: Date | string | null;
}

/**
 * Whether this subscription currently grants access.
 *
 * `past_due` counts. The payment failed and the provider is still retrying it, and all three give the
 * subscriber a window to fix their card without losing what they paid for — Apple and Google by their
 * own convention, Stripe by the dashboard automation that cancels on day 30. Treating that window as
 * locked-out was both harsher than the stores and money left on the table.
 *
 * A subscription whose window has already run out doesn't arrive here as `past_due`: it arrives
 * cancelled, because whatever wrote it stopped counting it as live.
 */
export const isSubscribed = (subscription: Subscription | null | undefined): boolean =>
  subscription?.status === 'active' || subscription?.status === 'past_due';

const asDate = (value: Date | string) => (value instanceof Date ? value : new Date(value));
const asWords = (value: Date | string) => format(asDate(value), 'MMMM d, yyyy');

/** The line shown under the Subscription heading, including the pitch made to a player without one. */
export function subscriptionMessage(subscription: Subscription | null): string {
  if (subscription === null) {
    return 'Access full archive of puzzles plus bonus features and early access to new games';
  }

  if (subscription.status === 'past_due') {
    // Access hasn't been lost yet, so this says what's true: it still works, and here is the date by
    // which it needs fixing. The date is missing whenever we're reading Stripe live rather than our
    // own record, so the sentence has to stand up without it.
    return subscription.graceEndsAt
      ? `Your last payment didn't go through. Update your payment method by ${asWords(subscription.graceEndsAt)} to keep access.`
      : "Your last payment didn't go through. Update your payment method to keep access.";
  }

  if (subscription.status === 'canceled') {
    return 'Your subscription is cancelled.';
  }

  if (subscription.status === 'active' && subscription.cancelAt !== null) {
    return `Your subscription is cancelled; you'll lose access on ${asWords(subscription.cancelAt)}.`;
  }

  return `Thanks for your subscription! Renews ${asWords(subscription.renewsAt)}`;
}

/** Where a `manage` action has to send the player, which is wherever they bought the subscription. */
export type ManageTarget = 'billing-portal' | 'app-store' | 'play-store';

export interface SubscriptionCTA {
  label: string;
  /** `create` starts a new subscription; `manage` sends them to `target`. */
  action: 'create' | 'manage';
  /** Whether this is the page's main call to action, and drawn as one. */
  primary: boolean;
  /** Only meaningful when `action` is `manage`. */
  target: ManageTarget;
}

/*
  A store subscription can only be changed in that store — we cannot cancel it, change the card, or
  refund it. Both pages open in a browser as well as on the device, so the website can send someone to
  the right place rather than offering them a billing portal that has never heard of them.
*/
export const APP_STORE_SUBSCRIPTIONS = 'https://apps.apple.com/account/subscriptions';
export const PLAY_STORE_SUBSCRIPTIONS = 'https://play.google.com/store/account/subscriptions';

export function manageTarget(provider: SubscriptionProvider | undefined): ManageTarget {
  if (provider === 'apple') return 'app-store';
  if (provider === 'google') return 'play-store';
  return 'billing-portal';
}

export function manageUrl(target: ManageTarget): string | null {
  if (target === 'app-store') return APP_STORE_SUBSCRIPTIONS;
  if (target === 'play-store') return PLAY_STORE_SUBSCRIPTIONS;
  // The billing portal needs a session minted server-side, so it has no fixed URL.
  return null;
}

/**
 * Whether this is a store subscription that will bill again. Selling this player a website
 * subscription would bill them twice, and only they can cancel the store one.
 */
export const renewsThroughStore = (subscription: Subscription | null | undefined): boolean =>
  !!subscription &&
  (subscription.provider === 'apple' || subscription.provider === 'google') &&
  isSubscribed(subscription) &&
  subscription.cancelAt === null;

/** The action to offer beside that line. */
export function subscriptionCTA(subscription: Subscription | null): SubscriptionCTA {
  const target = manageTarget(subscription?.provider);

  if (subscription === null) {
    return { label: 'Subscribe', action: 'create', primary: true, target };
  }

  // A past_due subscription is still live in Stripe; it needs a working card and its open invoice
  // paid, which is exactly what the billing portal does. Checkout would instead mint a *second*
  // subscription and leave the original grinding through Stripe's retry schedule, still emailing the
  // user about the card that failed.
  if (subscription.status === 'past_due') {
    return { label: 'Update Payment', action: 'manage', primary: true, target };
  }

  if (subscription.status === 'canceled') {
    return { label: 'Resubscribe', action: 'create', primary: true, target };
  }

  if (subscription.status === 'active' && subscription.cancelAt !== null) {
    return { label: 'Resubscribe', action: 'manage', primary: true, target };
  }

  return { label: 'Manage', action: 'manage', primary: false, target };
}

/*
  The App Store and Play products, whose ids are the plan keys Stripe prices carry as `lookup_key`, so
  a row reads the same whichever store sold it. Product ids are permanent once created in App Store
  Connect or the Play Console.
*/
export const STORE_PRODUCT_IDS = ['standard_monthly', 'standard_annual'] as const;

/*
  Where a player is sent to subscribe, and what they're told on the way.

  The prompts live here rather than in either app because they're the same pitch — and because
  `source` is what the funnel is measured by, so a second spelling of "highlighter" would quietly
  split a metric in two.
*/

/** What prompted the subscription, carried through to the checkout page for attribution. */
export type SubscriptionSource =
  | 'account'
  | 'highlighter'
  | 'archive'
  | 'solved'
  | 'solved_cta'
  | 'day_results'
  | 'challenge'
  | 'homepage';

/** The path to the subscribe flow. Native prefixes the site's origin; the website navigates to it. */
export function subscriptionPath(source: SubscriptionSource, gameType?: string): string {
  return `/subscription?source=${source}${gameType ? `&game_type=${gameType}` : ''}`;
}

/** The subscribe page's pitch, on the website and in the app's paywall. */
export const SUBSCRIPTION_PITCH = {
  title: 'Unlock Everything!',
  subtitle: ["Don't let Sunday's puzzle disappear.", 'Play any puzzle, any time.'],
  features: [
    'Early access to new games',
    'Access hundreds of past puzzles',
    'Exclusive features to help you solve',
    'Replay and track your times',
  ],
} as const;

/** Shown when a player without a subscription reaches for the highlighter. */
export const HIGHLIGHTER_PROMO = {
  title: 'Highlighter (subscriber perk)',
  body: 'Mark what you already know',
  cta: 'Subscribe',
} as const;

/** Shown to a non-subscriber on the archive. */
export const ARCHIVE_UNLOCK = { title: 'Unlock the archive' } as const;

/** The second line under it, naming how far back the archive goes when the game says. */
export function archiveUnlockSubtitle(earliest?: SimpleDate): string {
  const since = earliest ? ` since ${format(simpleDateToDate(earliest), 'MMM do')}` : '';
  return `Subscribe to access all puzzles${since}`;
}
